import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from "@/components/ui/select";
import { Loader2, UserPlus } from 'lucide-react';
import {
    useCreate,
    useGetList,
    useNotify,
    useRecordContext
} from 'ra-core';
import { useState } from 'react';
import type { Project } from '../types';

export const LinkVendorButton = () => {
    const record = useRecordContext<Project>();
    const [open, setOpen] = useState(false);
    const [vendorId, setVendorId] = useState<string>('');
    const [role, setRole] = useState('');
    const [loading, setLoading] = useState(false);
    
    const [create] = useCreate();
    const notify = useNotify();

    // Fetch approved vendors for the dropdown
    const { data: vendors, isPending } = useGetList('vendors', {
        filter: { status: 'approved' },
        pagination: { page: 1, perPage: 100 },
        sort: { field: 'name', order: 'ASC' }
    });

    if (!record) return null;

    const handleLink = async () => {
        if (!vendorId) return;
        setLoading(true);
        try {
            create(
                'project_vendors',
                {
                    data: {
                        project_id: record.id,
                        vendor_id: vendorId,
                        role: role || 'Supplier',
                        linked_at: new Date().toISOString()
                    }
                },
                {
                    onSuccess: () => {
                        notify('Vendor linked to project successfully', { type: 'success' });
                        setOpen(false);
                        setVendorId('');
                        setRole('');
                    },
                    onError: (error: any) => {
                        notify(`Error linking vendor: ${error.message}`, { type: 'error' });
                    }
                }
            );
        } catch (error: any) {
            notify(`Error: ${error.message}`, { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2">
                    <UserPlus className="w-4 h-4 text-primary" />
                    Link Vendor
                </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                    <DialogTitle>Link Vendor to Project</DialogTitle>
                    <DialogDescription>
                        Associate an approved vendor with this project and define their role.
                    </DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                    <div className="grid gap-2">
                        <Label htmlFor="vendor">Vendor</Label>
                        <Select onValueChange={setVendorId} value={vendorId}>
                            <SelectTrigger id="vendor">
                                <SelectValue placeholder={isPending ? "Loading vendors..." : "Select a vendor"} />
                            </SelectTrigger>
                            <SelectContent>
                                {vendors?.map((v) => (
                                    <SelectItem key={v.id} value={v.id.toString()}>
                                        {v.name} {v.code ? `(${v.code})` : ''}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="grid gap-2">
                        <Label htmlFor="role">Project Role</Label>
                        <Input
                            id="role"
                            placeholder="e.g. Primary Supplier, Consultant, etc."
                            value={role}
                            onChange={(e) => setRole(e.target.value)}
                        />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                    <Button onClick={handleLink} disabled={loading || !vendorId}>
                        {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Link Vendor
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
